import { InstalledPower } from "./InstalledPower";
import { InstallationAddress } from "./InstallationAddress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Building,
    MapPin,
    Phone,
    Mail,
    Calendar,
    Euro,
    Wrench,
    User
} from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { workOrderTypeLabel } from "@/lib/utils";
import { useLocation } from "wouter";
import {
    canViewContractEconomics,
    formatCents,
    summarizeContractCeiling,
    type ContractAdjustmentView,
    type ContractCeilingView,
    formatCalendarDate,
} from "./contractAdjustmentsUtils";
import { resolveEconomicMode } from "@/lib/installationContext";
import { InstallationEconomicSummary } from "@/components/installations/InstallationEconomicSummary";

export function OverviewTab({ installation }: { installation: any }) {
    const { user } = useAuth();
    const [, setLocation] = useLocation();
    const isTechnician = user?.role === 'technician';
    const canViewContract = canViewContractEconomics(user?.role);
    const economicMode = resolveEconomicMode(installation);
    const adjustmentsQuery = trpc.contractAdjustments.listByInstallation.useQuery(
        { installationId: installation.id },
        { enabled: canViewContract && economicMode !== "contract" },
    );
    const ceilingQuery = trpc.contractAdjustments.getEffectiveContractCeiling.useQuery(
        { installationId: installation.id },
        { enabled: canViewContract && economicMode !== "contract" },
    );
    const adjustments = ((adjustmentsQuery.data || []) as ContractAdjustmentView[]);
    const ceiling = (ceilingQuery.data || null) as ContractCeilingView;
    const contractSummary = summarizeContractCeiling(ceiling, installation.installationPrice, adjustments);
    const canonicalCustomerName = installation.canonicalCustomerName
        ?? (installation.contractId != null ? null : installation.clientName);
    const customerLabel = canonicalCustomerName
        ?? (installation.contractId != null ? "Cliente 360" : "Cliente pendiente de resolver");
    const initialCents = contractSummary.originalCents;
    const currentCents = contractSummary.effectiveContractCeilingCents;
    const pendingText = contractSummary.pendingCount === 1
        ? "1 modificación pendiente de aprobación"
        : contractSummary.pendingCount > 1
            ? `${contractSummary.pendingCount} modificaciones pendientes de aprobación`
            : null;

    return (
        <div className="grid min-w-0 grid-cols-1 gap-6 [overflow-wrap:anywhere] xl:grid-cols-2">
            {/* Client Info */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-primary">
                        <User className="w-5 h-5" />
                        Información del Cliente
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                        <InfoItem icon={<Building className="w-4 h-4" />} label="Cliente" value={customerLabel} />
                        <InfoItem icon={<MapPin className="w-4 h-4" />} label="Ubicación de la obra" value={<InstallationAddress installation={installation} />} />
                        <InfoItem icon={<Phone className="w-4 h-4" />} label="Teléfono" value={installation.clientPhone || "N/A"} />
                        <InfoItem icon={<Mail className="w-4 h-4" />} label="Email" value={installation.clientEmail || "N/A"} />
                        <InfoItem icon={<User className="w-4 h-4" />} label="DNI/CIF" value={installation.clientDocument || "N/A"} />
                    </div>
                </CardContent>
            </Card>

            {/* Project Info */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-600">
                        <Wrench className="w-5 h-5" />
                        Detalles del Proyecto
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                        <InfoItem
                            icon={<Wrench className="w-4 h-4" />}
                            label="Tipo Orden"
                            value={<Badge variant="secondary">{workOrderTypeLabel(installation.workOrderType)}</Badge>}
                        />
                        <InstalledPower installation={installation} canEdit={["admin", "admin_manager", "project_manager"].includes(user?.role ?? "")} />
                        <InfoItem icon={<Building className="w-4 h-4" />} label="Tipo Inst." value={installation.installationType} className="capitalize" />
                        <InfoItem
                            icon={<Calendar className="w-4 h-4" />}
                            label="Inicio"
                            value={installation.startDate ? formatCalendarDate(installation.startDate) : "Pendiente"}
                        />
                        <InfoItem
                            icon={<Calendar className="w-4 h-4" />}
                            label="Fin"
                            value={installation.endDate ? formatCalendarDate(installation.endDate) : "En curso"}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="xl:col-span-2">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-600">
                        <Euro className="w-5 h-5" />
                        {isTechnician ? "Mano de Obra" : "Datos Económicos"}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className={`grid min-w-0 grid-cols-1 gap-4 ${isTechnician ? "md:grid-cols-1" : "lg:grid-cols-3"}`}>
                        {!isTechnician && (
                            <>
                                <div className="min-w-0 p-4 bg-muted/30 rounded-lg border">
                                    <p className="text-sm text-muted-foreground mb-1">Nº de Presupuesto / Ref</p>
                                    <p className="break-words text-lg font-bold [overflow-wrap:anywhere]">{installation.budget || "N/A"}</p>
                                </div>
                                {economicMode === "contract" ? (
                                    <div className="min-w-0 col-span-full">
                                        <InstallationEconomicSummary installation={installation} />
                                    </div>
                                ) : (
                                    <>
                                        <div className="min-w-0 p-4 bg-muted/30 rounded-lg border">
                                            <p className="text-sm text-muted-foreground mb-1">Importe contractual vigente</p>
                                            {adjustmentsQuery.isLoading || ceilingQuery.isLoading ? (
                                                <p className="text-sm text-muted-foreground">Cargando...</p>
                                            ) : adjustmentsQuery.isError || ceilingQuery.isError ? (
                                                <p className="text-sm text-red-600">No se pudo cargar la información contractual.</p>
                                            ) : (
                                                <>
                                                    <p className="text-lg font-bold">{formatCents(currentCents)}</p>
                                                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                                                        <p>Inicial: {formatCents(initialCents)}</p>
                                                        {contractSummary.approvedDeltaCents === 0 ? (
                                                            <p>Sin modificaciones aprobadas</p>
                                                        ) : (
                                                            <p>Modificaciones aprobadas: {formatCents(contractSummary.approvedDeltaCents, { signed: true })}</p>
                                                        )}
                                                        {pendingText && <p>{pendingText}</p>}
                                                    </div>
                                                    <Button
                                                        variant="link"
                                                        className="mt-2 h-auto p-0"
                                                        onClick={() => setLocation(`/installations/${installation.id}?tab=billing&section=contract-adjustments`)}
                                                    >
                                                        Ver contrato y modificaciones →
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <div className="min-w-0 p-4 bg-muted/30 rounded-lg border">
                            <p className="text-sm text-muted-foreground mb-1">Coste mano de obra instaladores</p>
                            <p className="break-words text-lg font-bold [overflow-wrap:anywhere]">{installation.laborPrice ? `${installation.laborPrice} €` : "N/A"}</p>
                        </div>
                    </div>
                    {!isTechnician && (
                        <div className="mt-4">
                            <p className="text-sm font-medium mb-2">Descripción del Trabajo:</p>
                            <div className="min-w-0 p-3 bg-muted rounded text-sm text-muted-foreground [overflow-wrap:anywhere]">
                                {installation.workDescription || "Sin descripción detallada."}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function InfoItem({
    icon,
    label,
    value,
    className = "",
}: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    className?: string;
}) {
    return (
        <div className="min-w-0 rounded-md bg-muted/30 p-3">
            <div className="flex items-center gap-2 text-muted-foreground">
                {icon}
                <span>{label}</span>
            </div>
            <div className={`mt-1 break-words font-medium [overflow-wrap:anywhere] ${className}`}>
                {value}
            </div>
        </div>
    );
}
